'use strict';

/**
 * E23 Lakeshore — theme behaviors.
 *
 * No jQuery dependency for anything the theme controls. jQuery is used *only*
 * as a bridge for CRM embed events, and only if some other script has already
 * put it on the page — see onDocumentEvent() below.
 *
 * Page targeting is presence-based (does the element exist?) rather than
 * path-based. The previous version hardcoded `location.pathname === '/'` and
 * `=== '/spread-the-word/'`, which broke on subdirectory installs and on any
 * site that renamed those pages.
 */
(function () {
	// ---------------------------------------------------------------------
	// Helpers
	// ---------------------------------------------------------------------

	const qs = (sel, ctx) => (ctx || document).querySelector(sel);
	const qsa = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

	/**
	 * wp_localize_script() casts every value to a string, so PHP `true` arrives
	 * as "1" and PHP `false` arrives as "". Treat the falsy string forms as
	 * false. (If you switch to wp_add_inline_script + wp_json_encode — see the
	 * accompanying notes — real booleans will arrive and this still works.)
	 */
	const truthy = (v) =>
		v !== undefined && v !== null && v !== false && v !== 0 &&
		v !== '' && v !== '0' && v !== 'false';

	const ready = (fn) => {
		if (document.readyState !== 'loading') {
			fn();
		} else {
			document.addEventListener('DOMContentLoaded', fn);
		}
	};

	/**
	 * Action Network and ActionKit fire their lifecycle events through jQuery's
	 * event system. jQuery-triggered events do not reach native
	 * addEventListener handlers, so we bind both ways and let whichever one is
	 * live win. Handlers here are idempotent, so a double-fire is harmless.
	 */
	const onDocumentEvent = (name, handler) => {
		document.addEventListener(name, handler);
		if (window.jQuery) {
			window.jQuery(document).on(name, handler);
		}
	};

	const onDelegatedEvent = (selector, name, handler) => {
		if (window.jQuery) {
			window.jQuery(document).on(name, selector, handler);
		}
		document.addEventListener(name, (e) => {
			if (e.target && e.target.closest && e.target.closest(selector)) {
				handler(e);
			}
		}, true);
	};

	// ---------------------------------------------------------------------
	// Config
	// ---------------------------------------------------------------------

	const cfg = window.custom || {};
	const crmType = cfg.crm || '';
	const smsDisclaimer = truthy(cfg.disclaimer) ? cfg.disclaimer : '';

	// Some vendor/inline snippets read this global directly.
	window.smsDisclaimer = smsDisclaimer;

	// ---------------------------------------------------------------------
	// Popup
	// ---------------------------------------------------------------------

	// The popup should show again the first time a visitor arrives after
	// local midnight, not exactly 24 hours after they last saw it — so what
	// we store and compare is the visitor's local calendar date, not a
	// timestamp.
	const POPUP_STORAGE_KEY = 'e23_popup_last_shown';

	/** e.g. "2026-08-20", using the visitor's local time zone. */
	function localDateString() {
		const now = new Date();
		const y = now.getFullYear();
		const m = String(now.getMonth() + 1).padStart(2, '0');
		const d = String(now.getDate()).padStart(2, '0');
		return y + '-' + m + '-' + d;
	}

	/**
	 * Wrapped in try/catch because localStorage throws (rather than just
	 * being unavailable) in some private-browsing modes and in iframes with
	 * storage access blocked. Fail open — i.e. show the popup — rather than
	 * letting a storage error break the page.
	 */
	function popupSeenRecently() {
		try {
			return window.localStorage.getItem(POPUP_STORAGE_KEY) === localDateString();
		} catch (err) {
			return false;
		}
	}

	function markPopupSeen() {
		try {
			window.localStorage.setItem(POPUP_STORAGE_KEY, localDateString());
		} catch (err) {
			// Storage unavailable — nothing to do; popup will just show again
			// next visit.
		}
	}

	function dismissPopup() {
		const popup = qs('.popup');
		if (!popup) {
			return;
		}

		document.body.classList.remove('popup--active');
		document.body.classList.add('popup--inactive');
		popup.classList.add('popup--disable', 'popup--fading');

		const remove = () => popup.remove();
		popup.addEventListener('transitionend', remove, { once: true });
		// Fallback in case the element has no transition attached.
		setTimeout(remove, 400);
	}

	function initPopup() {
		if (!truthy(cfg.popup)) {
			return;
		}

		const popup = qs('.popup');
		if (!popup) {
			return;
		}

		// Already shown within the last 24 hours — remove it outright rather
		// than running the normal dismiss animation, so it never flashes on
		// screen for a returning visitor. Body still loads with
		// .popup--active from PHP (it doesn't know the popup was already
		// seen), so clear it the same way dismissPopup() does — otherwise
		// scrolling stays locked for the rest of the visit.
		if (popupSeenRecently()) {
			document.body.classList.remove('popup--active');
			document.body.classList.add('popup--inactive');
			popup.remove();
			return;
		}

		markPopupSeen();

		qsa('.popup__close, .popup__btn--text, .popup__content a.cta__btn')
			.forEach((el) => el.addEventListener('click', dismissPopup));

		if (crmType === 'action-network') {
			onDocumentEvent('web-popup_submitted', dismissPopup);
		}

		if (crmType === 'actionkit') {
			onDelegatedEvent('.popup form', 'ajax:complete', dismissPopup);
		}
	}

	// ---------------------------------------------------------------------
	// Action Network — email / zip pairing
	// ---------------------------------------------------------------------

	const PAIR_STACK_QUERY = '(max-width: 600px)';
	const PAIR_GAP = 6; // px
	const PAIR_EMAIL_WIDTH = 60; // %

	// Every pair discovered so far, so the breakpoint listener can re-apply.
	const pairs = [];

	const isHidden = (el) => {
		if (el.hidden) {
			return true;
		}
		const style = getComputedStyle(el);
		// `display` is not inherited, so a child of a display:none popup still
		// reports its own value here.
		return style.display === 'none';
	};

	const nextVisible = (el) => {
		let sib = el.nextElementSibling;
		while (sib && isHidden(sib)) {
			sib = sib.nextElementSibling;
		}
		return sib;
	};

	/**
	 * Given two inputs, walk up both ancestor chains to find the pair of
	 * elements that are siblings of one another — i.e. the "row" wrappers,
	 * whatever tag Action Network happens to be using.
	 *
	 * This is the part the CSS :has() version got wrong: it assumed the rows
	 * were <li> elements directly inside a <ul>.
	 */
	function findSiblingRows(a, b, boundary) {
		const chainA = [];
		for (let el = a; el && el !== boundary; el = el.parentElement) {
			chainA.push(el);
		}

		for (let elB = b; elB && elB !== boundary; elB = elB.parentElement) {
			const parent = elB.parentElement;
			if (!parent) {
				break;
			}

			const elA = chainA.find((x) => x.parentElement === parent && x !== elB);
			if (elA) {
				return { container: parent, rowA: elA, rowB: elB };
			}
		}

		return null;
	}

	/**
	 * Widths are set inline with `important` priority because Action Network's
	 * own stylesheet loads after the theme's and is aggressive about form
	 * layout. Inline + important is the only declaration that reliably wins.
	 */
	function applyPairLayout() {
		const stack = window.matchMedia(PAIR_STACK_QUERY).matches;
		const half = PAIR_GAP / 2;

		pairs.forEach((pair) => {
			const { container, emailRow, zipRow } = pair;

			if (!container.isConnected) {
				return;
			}

			if (stack) {
				['display', 'flex-wrap', 'column-gap'].forEach((prop) =>
					container.style.removeProperty(prop)
				);
				Array.from(container.children).forEach((child) =>
					child.style.removeProperty('flex')
				);
				return;
			}

			container.style.setProperty('display', 'flex', 'important');
			container.style.setProperty('flex-wrap', 'wrap', 'important');
			container.style.setProperty('column-gap', PAIR_GAP + 'px', 'important');

			// Everything that is not part of the pair keeps stacking.
			Array.from(container.children).forEach((child) => {
				if (child !== emailRow && child !== zipRow) {
					child.style.setProperty('flex', '0 0 100%', 'important');
				}
			});

			emailRow.style.setProperty(
				'flex',
				'0 0 calc(' + PAIR_EMAIL_WIDTH + '% - ' + half + 'px)',
				'important'
			);
			zipRow.style.setProperty(
				'flex',
				'0 0 calc(' + (100 - PAIR_EMAIL_WIDTH) + '% - ' + half + 'px)',
				'important'
			);

			// The inputs themselves need to fill their new row width.
			[pair.email, pair.zip].forEach((input) => {
				input.style.setProperty('width', '100%', 'important');
				input.style.setProperty('box-sizing', 'border-box', 'important');
			});
		});
	}

	function pairEmailAndZip() {
		// Attribute selectors rather than #id: Action Network emits duplicate
		// IDs when more than one form is embedded on the same page.
		qsa('input[id="form-email"]').forEach((email) => {
			const scope = email.closest('form') ||
				email.closest('.can_embed') ||
				email.closest('#can_embed_form');

			if (!scope) {
				return;
			}

			const zip = scope.querySelector('input[id="form-zip_code"]');
			if (!zip || zip === email) {
				return;
			}

			// Already handled on a previous pass.
			if (pairs.some((p) => p.email === email)) {
				return;
			}

			const rows = findSiblingRows(email, zip, scope.parentElement);
			if (!rows) {
				return;
			}

			const emailRow = rows.rowA.contains(email) ? rows.rowA : rows.rowB;
			const zipRow = emailRow === rows.rowA ? rows.rowB : rows.rowA;

			// Only pair them if they are visually adjacent, ignoring any
			// hidden fields Action Network leaves in the markup.
			if (nextVisible(emailRow) !== zipRow && nextVisible(zipRow) !== emailRow) {
				return;
			}

			rows.container.classList.add('an-pair');
			emailRow.classList.add('an-pair__email');
			zipRow.classList.add('an-pair__zip');

			pairs.push({
				container: rows.container,
				emailRow: emailRow,
				zipRow: zipRow,
				email: email,
				zip: zip,
			});
		});

		applyPairLayout();
	}

	/**
	 * Action Network re-renders the form on validation errors without firing
	 * another web-*_loaded event, so watch for it rather than chasing event
	 * names.
	 */
	function watchActionNetworkForms() {
		if (typeof MutationObserver === 'undefined') {
			return;
		}

		const roots = qsa('#can_embed_form, .can_embed');
		if (!roots.length) {
			return;
		}

		let pending;
		const observer = new MutationObserver(() => {
			clearTimeout(pending);
			pending = setTimeout(() => {
				// Drop any pairs whose nodes have been replaced.
				for (let i = pairs.length - 1; i >= 0; i--) {
					if (!pairs[i].email.isConnected) {
						pairs.splice(i, 1);
					}
				}
				pairEmailAndZip();
			}, 100);
		});

		roots.forEach((root) =>
			observer.observe(root, { childList: true, subtree: true })
		);
	}

	// ---------------------------------------------------------------------
	// Action Network embedded form cleanup
	// ---------------------------------------------------------------------

	const AN_PLACEHOLDERS = {
		'input#form-email': 'Email Address*',
		'input#form-zip_code': 'Zip Code*',
		'input#form-phone': 'Mobile Number',
	};

	const AN_REMOVE = [
		'#can_embed_form_inner > h2',
		'#can_embed_form_inner > h3',
		'#can_embed_form_inner > h4',
		'li.form_builder_output',
		'#action_info',
		'#logo_wrap',
		'span.international_link-wrap',
	];

	/**
	 * Was duplicated verbatim five times in the previous version.
	 * @param {{removeCountryDrop?: boolean}} [options]
	 */
	function patchActionNetworkForm(options) {
		const opts = options || {};

		AN_REMOVE.forEach((sel) => qsa(sel).forEach((el) => el.remove()));

		if (opts.removeCountryDrop) {
			qsa('span.country_drop_wrap').forEach((el) => el.remove());
		}

		Object.keys(AN_PLACEHOLDERS).forEach((sel) => {
			const input = qs(sel);
			if (input) {
				input.placeholder = AN_PLACEHOLDERS[sel];
			}
		});

		const smsGroup = qs('input[name="subscription[sms_group]"]');
		if (smsGroup) {
			smsGroup.checked = true;
		}

		if (smsDisclaimer) {
			const target = qs('#mobile_opt_in_default');
			if (target) {
				target.innerHTML = smsDisclaimer;
			}
		}

		// jQuery's .css() cannot set !important, so the original line silently
		// did nothing. setProperty() takes the priority as a third argument.
		const zip = qs('#can_embed_form .core_field #form-zip_code:not(.mb_needed)');
		if (zip) {
			zip.style.setProperty('margin-bottom', '6px', 'important');
		}

		// Runs last: the removals above change sibling order, and adjacency is
		// what decides whether the pair gets applied.
		pairEmailAndZip();
		watchActionNetworkForms();
	}

	function initActionNetwork() {
		if (crmType !== 'action-network') {
			return;
		}

		['web-popup_loaded', 'web-featured_loaded', 'web-ad_loaded', 'web-footer-cta_loaded']
			.forEach((name) => onDocumentEvent(name, () => patchActionNetworkForm()));

		onDocumentEvent('web-splash_loaded', () =>
			patchActionNetworkForm({ removeCountryDrop: true })
		);

		const mq = window.matchMedia(PAIR_STACK_QUERY);
		if (mq.addEventListener) {
			mq.addEventListener('change', applyPairLayout);
		} else if (mq.addListener) {
			mq.addListener(applyPairLayout);
		}
	}

	// ---------------------------------------------------------------------
	// Alert tray
	// ---------------------------------------------------------------------

	function initAlertTray() {
		if (!qs('#alert.alert__type--contrib')) {
			return;
		}

		setTimeout(() => {
			qsa('.alert__type--contrib').forEach((el) => el.classList.add('is-visible'));
		}, 1500);
	}

	// ---------------------------------------------------------------------
	// Mobile navigation
	// ---------------------------------------------------------------------

	function initMobileNav() {
		const trigger = qs('.header__nav--mobile');
		const hamburger = qs('.hamburger');
		const nav = qs('.header__nav');
		const wrap = qs('.header__nav--wrap');

		if (!trigger || !hamburger || !nav || !wrap) {
			return;
		}

		trigger.setAttribute('aria-expanded', 'false');

		trigger.addEventListener('click', () => {
			const open = hamburger.classList.toggle('is-active');

			wrap.classList.toggle('is-active', open);
			// Replaces the inline display:flex / display:none the old version set.
			nav.classList.toggle('header__nav--open', open);
			trigger.setAttribute('aria-expanded', String(open));
		});
	}

	// ---------------------------------------------------------------------
	// ZIP → lat/lon
	// ---------------------------------------------------------------------

	function initGeocode() {
		const address = qs('#address');
		const latField = qs('input[name="lat"]');
		const lonField = qs('input[name="lon"]');

		if (!address || !latField || !lonField) {
			return;
		}

		// Not currently passed through wp_localize_script — see notes.
		const apiKey = cfg.googleAPI;
		if (!apiKey) {
			return;
		}

		let timer;
		let controller;

		address.addEventListener('input', () => {
			clearTimeout(timer);

			const zip = address.value.trim();
			if (!/^\d{5}$/.test(zip)) {
				return;
			}

			timer = setTimeout(() => {
				if (controller) {
					controller.abort();
				}
				controller = new AbortController();

				const url = new URL('https://maps.googleapis.com/maps/api/geocode/json');
				url.searchParams.set('address', zip);
				url.searchParams.set('key', apiKey);

				fetch(url, { signal: controller.signal })
					.then((res) => res.json())
					.then((data) => {
						const loc = data && data.results && data.results[0] &&
							data.results[0].geometry && data.results[0].geometry.location;
						if (!loc) {
							return;
						}
						latField.value = loc.lat;
						lonField.value = loc.lng;
					})
					.catch((err) => {
						if (err.name !== 'AbortError') {
							console.error('Geocode lookup failed:', err);
						}
					});
			}, 400);
		});
	}

	// ---------------------------------------------------------------------
	// Recurring contribution toggle
	// ---------------------------------------------------------------------

	const RECURRING_PARAM = {
		'secure.actblue.com': 'recurring',
		'numero.ai': 'recurring',
		'actionnetwork.org': 'recurring',
		'secure.ngpvan.com': 'r',
		'secure.everyaction.com': 'r',
	};

	function setRecurring(on) {
		qsa('.footer-cta__contrib a.cta__btn').forEach((link) => {
			const param = RECURRING_PARAM[link.hostname];
			if (!param) {
				return;
			}

			let url;
			try {
				url = new URL(link.href);
			} catch (err) {
				return;
			}

			// Only rewrite links that already declare the parameter, matching
			// the previous behavior.
			if (!url.searchParams.has(param)) {
				return;
			}

			url.searchParams.set(param, on ? 'true' : 'false');
			link.href = url.toString();
		});
	}

	function initRecurring() {
		qsa('.cta__monthly--btn').forEach((btn) => {
			btn.addEventListener('click', () => {
				qsa('.cta__monthly--btn.cta__monthly--btn-active')
					.forEach((b) => b.classList.remove('cta__monthly--btn-active'));
				btn.classList.add('cta__monthly--btn-active');
			});
		});

		const yes = qs('#monthly-yes');
		const no = qs('#monthly-no');

		if (yes) {
			yes.addEventListener('click', () => setRecurring(true));
		}
		if (no) {
			no.addEventListener('click', () => setRecurring(false));
		}
	}

	// ---------------------------------------------------------------------
	// Media filter
	// ---------------------------------------------------------------------

	function initMediaFilter() {
		qsa('[name="filter"]').forEach((el) => {
			el.addEventListener('change', () => {
				if (el.value) {
					window.location.href = el.value;
				}
			});
		});
	}

	// ---------------------------------------------------------------------
	// Toolkit share links
	// ---------------------------------------------------------------------

	function initToolkitShare() {
		const links = qsa('.wp-block-toolkit-sm__actions--link');
		if (!links.length) {
			return;
		}

		links.forEach((link) => {
			link.addEventListener('click', function (e) {
				e.preventDefault();

				if (this.href && this.href.indexOf('facebook.com') !== -1) {
					const deck = this.closest('.wp-block-toolkit-sm__deck');
					if (deck && navigator.clipboard) {
						navigator.clipboard.writeText(deck.textContent.trim())
							.catch((err) => console.error('Copy failed:', err));
					}
				}

				const width = 550;
				const height = 640;
				const left = (screen.width - width) / 2;
				const top = (screen.height - height) / 2;

				window.open(
					this.href,
					'socialPopup',
					'width=' + width + ',height=' + height +
					',left=' + left + ',top=' + top +
					',resizable=yes,scrollbars=yes'
				);
			});
		});
	}

	// ---------------------------------------------------------------------
	// Vendor hooks
	// ---------------------------------------------------------------------

	function initLightboxAttributes() {
		qsa([
			'.wp-block-image img',
			'.wp-block-media-text__media img',
			'.wp-block-timeline img',
			'.wp-block-endorse img',
		].join(', ')).forEach((img) => img.setAttribute('data-lity', ''));
	}

	function initAos() {
		if (qs('[data-aos]') && typeof window.AOS !== 'undefined') {
			window.AOS.init({ easing: 'ease', duration: 1500 });
		}
	}

	ready(() => {
		initPopup();
		initActionNetwork();
		initAlertTray();
		initMobileNav();
		initGeocode();
		initRecurring();
		initMediaFilter();
		initToolkitShare();
		initLightboxAttributes();
		initAos();
	});
}());